import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/useEntity';
import { Modal } from './ui/Modal';
import { Button } from './ui/Button';
import { Spinner } from './ui/Spinner';
import { Textarea } from './ui/Input';
import { FormRow } from './ui/FormRow';

type ImagenScrape = {
  url: string;
  tipo: 'principal' | 'galeria';
  alt: string | null;
};

type ScrapePreview = {
  source_url: string;
  final_url: string;
  titulo: string | null;
  descripcion_html: string | null;
  descripcion_texto: string | null;
  imagenes: ImagenScrape[];
};

type ApplyResult = {
  descripcion_aplicada: boolean;
  imagenes_aplicadas: number;
  imagenes_fallidas: number;
  resultados: Array<{ url: string; ok: boolean; error?: string; bytes?: number }>;
};

export function ProductoScrapeModal({
  productoId,
  urlOficial,
  open,
  onClose,
}: {
  productoId: string;
  urlOficial: string | null;
  open: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [preview, setPreview] = useState<ScrapePreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [descripcionEdit, setDescripcionEdit] = useState('');
  const [aplicarDescripcion, setAplicarDescripcion] = useState(true);
  const [seleccion, setSeleccion] = useState<Set<string>>(new Set());
  const [resultado, setResultado] = useState<ApplyResult | null>(null);

  const previewMut = useMutation({
    mutationFn: () => api.post<ScrapePreview>(`/productos/${productoId}/scrape/preview`, {}),
    onSuccess: (data) => {
      setPreview(data);
      setError(null);
      setDescripcionEdit(data.descripcion_html ?? data.descripcion_texto ?? '');
      setAplicarDescripcion(Boolean(data.descripcion_html ?? data.descripcion_texto));
      setSeleccion(new Set(data.imagenes.map((i) => i.url)));
    },
    onError: (err: Error & { data?: { message?: string } }) => {
      setError(err.data?.message ?? err.message ?? 'Error desconocido');
    },
  });

  const aplicarMut = useMutation({
    mutationFn: () =>
      api.post<ApplyResult>(`/productos/${productoId}/scrape/aplicar`, {
        source_url: preview?.final_url,
        aplicar_descripcion: aplicarDescripcion,
        descripcion_html: aplicarDescripcion ? descripcionEdit : null,
        imagenes_urls: Array.from(seleccion),
      }),
    onSuccess: (data) => {
      setResultado(data);
      qc.invalidateQueries({ queryKey: ['producto', productoId, 'full'] });
      qc.invalidateQueries({ queryKey: ['producto-archivos', productoId] });
    },
    onError: (err: Error & { data?: { message?: string } }) => {
      setError(err.data?.message ?? err.message ?? 'Error desconocido');
    },
  });

  // Lanzar preview al abrir
  useEffect(() => {
    if (open && !preview && !previewMut.isPending) {
      previewMut.mutate();
    }
    if (!open) {
      // Reset al cerrar
      setPreview(null);
      setError(null);
      setDescripcionEdit('');
      setAplicarDescripcion(true);
      setSeleccion(new Set());
      setResultado(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const toggleImagen = (url: string) => {
    setSeleccion((s) => {
      const next = new Set(s);
      if (next.has(url)) next.delete(url);
      else next.add(url);
      return next;
    });
  };

  const seleccionarTodas = () => setSeleccion(new Set(preview?.imagenes.map((i) => i.url) ?? []));
  const deseleccionarTodas = () => setSeleccion(new Set());

  const principal = useMemo(
    () => preview?.imagenes.find((i) => i.tipo === 'principal') ?? null,
    [preview],
  );
  const galeria = useMemo(
    () => preview?.imagenes.filter((i) => i.tipo === 'galeria') ?? [],
    [preview],
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Importar ficha desde URL oficial"
      size="xl"
      footer={
        resultado ? (
          <Button onClick={onClose}>Cerrar</Button>
        ) : (
          <>
            <Button variant="secondary" onClick={onClose} disabled={aplicarMut.isPending}>
              Cancelar
            </Button>
            <Button
              variant="save"
              onClick={() => aplicarMut.mutate()}
              disabled={
                !preview || aplicarMut.isPending
                  || (!aplicarDescripcion && seleccion.size === 0)
              }
            >
              {aplicarMut.isPending
                ? 'Aplicando…'
                : `Aplicar (${aplicarDescripcion ? 'descripción + ' : ''}${seleccion.size} imagen${seleccion.size === 1 ? '' : 'es'})`}
            </Button>
          </>
        )
      }
    >
      {!urlOficial && (
        <p className="text-sm text-rose-700">
          El producto no tiene URL oficial. Edítalo y añade la URL antes de importar.
        </p>
      )}

      {urlOficial && previewMut.isPending && (
        <div className="flex items-center gap-3 py-6">
          <Spinner />
          <span className="text-sm text-slate-600">Descargando y parseando la página…</span>
        </div>
      )}

      {error && (
        <div className="mb-3 rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-800 ring-1 ring-rose-200">
          {error}
        </div>
      )}

      {resultado && (
        <div className="space-y-3">
          <div className="rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-800 ring-1 ring-emerald-200">
            {resultado.descripcion_aplicada && 'Descripción aplicada. '}
            {resultado.imagenes_aplicadas > 0 && `${resultado.imagenes_aplicadas} imagen(es) subida(s). `}
            {resultado.imagenes_fallidas > 0 && `${resultado.imagenes_fallidas} fallaron.`}
          </div>
          {resultado.imagenes_fallidas > 0 && (
            <ul className="space-y-1 text-xs text-slate-600">
              {resultado.resultados
                .filter((r) => !r.ok)
                .map((r) => (
                  <li key={r.url}>
                    <span className="font-mono">{r.url}</span> — {r.error}
                  </li>
                ))}
            </ul>
          )}
        </div>
      )}

      {!resultado && preview && (
        <div className="space-y-4">
          <div className="text-xs text-slate-500">
            Fuente: <a href={preview.final_url} target="_blank" rel="noreferrer" className="text-sky-600 hover:underline">{preview.final_url}</a>
            {preview.titulo && (<> · <span className="font-medium text-slate-700">{preview.titulo}</span></>)}
          </div>

          <div>
            <div className="mb-2 flex items-center gap-2">
              <input
                id="aplicar-desc"
                type="checkbox"
                checked={aplicarDescripcion}
                onChange={(e) => setAplicarDescripcion(e.target.checked)}
                className="cursor-pointer"
              />
              <label htmlFor="aplicar-desc" className="cursor-pointer text-sm font-semibold text-slate-700">
                Aplicar descripción
              </label>
            </div>
            <FormRow label="Descripción importada (editable)">
              <Textarea
                rows={10}
                value={descripcionEdit}
                onChange={(e) => setDescripcionEdit(e.target.value)}
                disabled={!aplicarDescripcion}
                className="font-mono text-xs"
                placeholder={preview.descripcion_html ? '' : 'No se ha encontrado descripción en la página.'}
              />
            </FormRow>
            <p className="mt-1 text-xs text-slate-500">
              Contiene HTML rico (p, strong, ul, li). El campo se guarda tal cual en <span className="font-mono">productos.ficha_descripcion</span>.
            </p>
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between">
              <p className="text-sm font-semibold text-slate-700">
                Imágenes a importar ({seleccion.size}/{preview.imagenes.length})
              </p>
              <div className="flex gap-2 text-xs">
                <button type="button" onClick={seleccionarTodas} className="cursor-pointer text-sky-600 hover:underline">
                  Todas
                </button>
                <button type="button" onClick={deseleccionarTodas} className="cursor-pointer text-slate-500 hover:underline">
                  Ninguna
                </button>
              </div>
            </div>

            {preview.imagenes.length === 0 ? (
              <p className="text-sm italic text-slate-400">No se han detectado imágenes en la página.</p>
            ) : (
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                {principal && (
                  <ImagenCard
                    key={principal.url}
                    img={principal}
                    selected={seleccion.has(principal.url)}
                    onToggle={() => toggleImagen(principal.url)}
                    badge="Principal"
                  />
                )}
                {galeria.map((img, idx) => (
                  <ImagenCard
                    key={img.url}
                    img={img}
                    selected={seleccion.has(img.url)}
                    onToggle={() => toggleImagen(img.url)}
                    badge={`Galería ${idx + 1}`}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}

function ImagenCard({
  img,
  selected,
  onToggle,
  badge,
}: {
  img: ImagenScrape;
  selected: boolean;
  onToggle: () => void;
  badge: string;
}) {
  return (
    <label
      className={`group flex cursor-pointer flex-col overflow-hidden rounded-md border ring-1 transition ${
        selected
          ? 'border-sky-500 bg-sky-50 ring-sky-300'
          : 'border-slate-200 ring-transparent hover:border-slate-300'
      }`}
    >
      <div className="relative aspect-square w-full overflow-hidden bg-slate-100">
        <img
          src={img.url}
          alt={img.alt ?? ''}
          loading="lazy"
          className="absolute inset-0 h-full w-full object-contain"
          referrerPolicy="no-referrer"
        />
        <div className="absolute left-1.5 top-1.5 rounded bg-slate-900/70 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">
          {badge}
        </div>
        <input
          type="checkbox"
          checked={selected}
          onChange={onToggle}
          className="absolute right-1.5 top-1.5 h-4 w-4 cursor-pointer"
        />
      </div>
    </label>
  );
}
